import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { shiftTemplateAPI } from '../../services';

/**
 * Sửa các CA đang gắn cho MỘT task, ngay tại màn Danh sách task (#110145).
 *
 * #221348 — đổi từ "chọn từng ca + Thêm" sang DANH SÁCH Ô TICK:
 * - Danh sách chính = mọi ca ĐANG HOẠT ĐỘNG của chi nhánh đang chọn (`branchId`);
 *   chưa chọn chi nhánh thì hiện ca đang hoạt động của mọi chi nhánh, nhóm theo chi nhánh.
 * - Ca đã gắn thì tick sẵn. Ca đang gắn mà nằm ngoài danh sách chính (chi nhánh khác /
 *   tạm dừng) hiện ở mục "Ca khác đang gắn", tick sẵn — không tick lại thì KHÔNG bị gỡ.
 * - Tick/bỏ tick chỉ đổi trên màn hình; nút "Lưu (gắn X · gỡ Y)" mới ghi, gọi TUẦN TỰ
 *   đúng các API LẺ (`POST|DELETE /shift-templates/task-links`), KHÔNG dùng
 *   `setTemplateTasks()` — API đó ghi đè cả danh sách task của ca nên hai người sửa cùng
 *   lúc sẽ xoá mất task của nhau. Lỗi ở ca nào thì báo đúng ca đó, các ca khác vẫn lưu.
 * - Ô "Thứ tự" của ca đã gắn vẫn lưu ngay khi rời ô như trước (`PATCH`).
 *
 * Người không phải MANAGER/ADMIN chỉ XEM: ô tick + nút Lưu bị khoá (BE cũng chặn lại
 * bằng `RolesGuard`, không tin FE).
 */

const hhmm = (h, m) => `${String(h ?? 0).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;

const byStartTime = (a, b) =>
  (a.start_hour ?? 0) * 60 + (a.start_minute ?? 0) - ((b.start_hour ?? 0) * 60 + (b.start_minute ?? 0));

const messageOf = (err, fallback) =>
  err?.response?.data?.error?.message ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const ROW_STYLE = { fontSize: 16, cursor: 'pointer', minHeight: 56 };
const CHECK_STYLE = { width: 22, height: 22, flex: '0 0 auto', cursor: 'pointer' };

export default function TaskShiftLinksModal({
  show,
  onHide,
  task,
  templates = [],
  branchId = '',
  branchName = '',
  canEdit = false,
  onChanged,
}) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Tập shift_template_id người dùng MUỐN gắn (tick) — chỉ ghi khi bấm Lưu.
  const [checked, setChecked] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const selectAllRef = useRef(null);

  // '__global__' (việc dùng chung) không phải chi nhánh ⇒ coi như chưa chọn chi nhánh.
  const scopeBranchId = branchId && branchId !== '__global__' ? branchId : '';

  const linkByTemplate = useMemo(() => {
    const m = new Map();
    links.forEach((l) => m.set(l.shift_template_id, l));
    return m;
  }, [links]);

  // Danh sách chính: ca ĐANG HOẠT ĐỘNG của chi nhánh đang chọn (hoặc mọi chi nhánh).
  const mainTemplates = useMemo(
    () =>
      (templates || [])
        .filter((t) => t.is_active !== false)
        .filter((t) => !scopeBranchId || t.branch_id === scopeBranchId)
        .sort(
          (a, b) =>
            String(a.branch_name || '').localeCompare(String(b.branch_name || ''), 'vi') ||
            byStartTime(a, b),
        ),
    [templates, scopeBranchId],
  );
  const mainIds = useMemo(() => new Set(mainTemplates.map((t) => t.id)), [mainTemplates]);

  // Nhóm theo chi nhánh khi chưa chọn chi nhánh (AC12).
  const mainGroups = useMemo(() => {
    if (scopeBranchId) return [{ key: scopeBranchId, title: null, items: mainTemplates }];
    const groups = [];
    mainTemplates.forEach((t) => {
      const key = t.branch_id || '__none__';
      let g = groups.find((x) => x.key === key);
      if (!g) {
        g = { key, title: t.branch_name || 'Chưa rõ chi nhánh', items: [] };
        groups.push(g);
      }
      g.items.push(t);
    });
    return groups;
  }, [mainTemplates, scopeBranchId]);

  // Ca đang gắn nhưng không nằm trong danh sách chính (chi nhánh khác / tạm dừng) — AC6.
  const otherLinks = useMemo(
    () =>
      links
        .filter((l) => !mainIds.has(l.shift_template_id))
        .sort(
          (a, b) =>
            String(a.branch_name || '').localeCompare(String(b.branch_name || ''), 'vi') ||
            byStartTime(a, b),
        ),
    [links, mainIds],
  );

  const toAdd = useMemo(
    () => [...checked].filter((id) => !linkByTemplate.has(id)),
    [checked, linkByTemplate],
  );
  const toRemove = useMemo(
    () => links.filter((l) => !checked.has(l.shift_template_id)),
    [links, checked],
  );
  const dirty = toAdd.length > 0 || toRemove.length > 0;

  const checkedInMain = mainTemplates.filter((t) => checked.has(t.id)).length;
  const allState =
    checkedInMain === 0 ? 'none' : checkedInMain === mainTemplates.length ? 'all' : 'some';

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = allState === 'some';
  }, [allState, loading]);

  const loadLinks = async (taskId) => {
    try {
      setLoading(true);
      setError(null);
      const res = await shiftTemplateAPI.getTaskShiftLinks({ task_ids: [taskId] });
      const list = res?.data?.[taskId] || [];
      setLinks(list);
      return list;
    } catch (err) {
      console.error('Error loading task-shift links:', err);
      setError('Không tải được danh sách ca của task này');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Mở popup: tick sẵn đúng các ca đang gắn thật. Đóng mà chưa Lưu thì không ghi gì (AC7).
  useEffect(() => {
    if (show && task?.id) {
      setSaveErrors([]);
      setLinks([]);
      setChecked(new Set());
      loadLinks(task.id).then((list) => {
        if (list) setChecked(new Set(list.map((l) => l.shift_template_id)));
      });
    }
  }, [show, task?.id]);

  // Báo cho màn cha biết dữ liệu đã đổi để nó tải lại cột "Ca áp dụng".
  const notifyChanged = () => {
    if (typeof onChanged === 'function') onChanged();
  };

  const toggle = (templateId) => {
    if (!canEdit || saving) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!canEdit || saving) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (allState === 'all') mainTemplates.forEach((t) => next.delete(t.id));
      else mainTemplates.forEach((t) => next.add(t.id));
      return next;
    });
  };

  const templateById = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((t) => m.set(t.id, t));
    return m;
  }, [templates]);

  const nameOf = (templateId) => {
    const t = templateById.get(templateId) || linkByTemplate.get(templateId);
    if (!t) return templateId;
    const name = t.name || t.template_name || templateId;
    return t.branch_name ? `${name} · ${t.branch_name}` : name;
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    if (
      toRemove.length > 0 &&
      !window.confirm(
        `Gỡ task "${task?.name}" khỏi ${toRemove.length} ca: ` +
          `${toRemove.map((l) => nameOf(l.shift_template_id)).join(', ')}?\n\n` +
          'Các ca đang mở đã sinh việc thì vẫn giữ nguyên việc cũ; thay đổi chỉ áp dụng cho ca mở sau này.',
      )
    ) {
      return;
    }
    setSaving(true);
    setSaveErrors([]);
    const failures = [];
    let ok = 0;
    // Tuần tự từng ca: một ca lỗi (vd người khác vừa gắn trùng) không chặn các ca còn lại.
    for (const templateId of toAdd) {
      try {
        await shiftTemplateAPI.addTaskShiftLink({ task_id: task.id, shift_template_id: templateId });
        ok += 1;
      } catch (err) {
        console.error('Error adding task-shift link:', err);
        failures.push({ name: nameOf(templateId), action: 'gắn', message: messageOf(err, 'Không gắn được') });
      }
    }
    for (const link of toRemove) {
      try {
        await shiftTemplateAPI.deleteTaskShiftLink(link.id);
        ok += 1;
      } catch (err) {
        console.error('Error removing task-shift link:', err);
        failures.push({
          name: nameOf(link.shift_template_id),
          action: 'gỡ',
          message: messageOf(err, 'Không gỡ được'),
        });
      }
    }
    // Tải lại theo dữ liệu THẬT, tick lại đúng như máy chủ đang lưu (AC8).
    const list = await loadLinks(task.id);
    if (list) setChecked(new Set(list.map((l) => l.shift_template_id)));
    setSaving(false);
    setSaveErrors(failures);
    if (ok > 0) notifyChanged();
    if (failures.length === 0) toast.success(`Đã lưu ${ok} thay đổi ca áp dụng`);
    else toast.error(`Lưu được ${ok}, lỗi ${failures.length} ca — xem chi tiết trong popup`);
  };

  const handleChangeOrder = async (link, rawValue) => {
    const orderNo = Number(rawValue);
    if (!Number.isFinite(orderNo) || orderNo === link.order_no) return;
    try {
      setBusyOrderId(link.id);
      await shiftTemplateAPI.updateTaskShiftLink(link.id, { order_no: orderNo });
      await loadLinks(task.id);
      notifyChanged();
    } catch (err) {
      console.error('Error changing task-shift order:', err);
      toast.error(messageOf(err, 'Không đổi được thứ tự'));
      await loadLinks(task.id);
    } finally {
      setBusyOrderId(null);
    }
  };

  const renderRow = ({ templateId, title, subtitle, isOther }) => {
    const link = linkByTemplate.get(templateId);
    const isChecked = checked.has(templateId);
    const willAdd = isChecked && !link;
    const willRemove = !isChecked && !!link;
    const inputId = `tsl-${task?.id}-${templateId}`;
    return (
      <div
        key={templateId}
        className={`border rounded d-flex align-items-center gap-2 px-2 ${isChecked ? 'bg-primary bg-opacity-10 border-primary-subtle' : ''}`}
        data-testid={`tsl-row-${templateId}`}
      >
        <label
          htmlFor={inputId}
          className="d-flex align-items-center gap-2 flex-grow-1 py-2 mb-0"
          style={{ ...ROW_STYLE, cursor: canEdit ? 'pointer' : 'default', minWidth: 0 }}
        >
          <input
            id={inputId}
            type="checkbox"
            className="form-check-input m-0"
            style={CHECK_STYLE}
            checked={isChecked}
            disabled={!canEdit || saving}
            onChange={() => toggle(templateId)}
          />
          <span className="flex-grow-1" style={{ minWidth: 0 }}>
            {/* Xuống dòng thay vì cắt "…": trên điện thoại tên ca kèm tên chi nhánh dài hơn 1 dòng. */}
            <span className="d-block text-break">{title}</span>
            <small className="d-block text-muted text-break" style={{ fontSize: 13 }}>
              {subtitle}
              {link && !isOther ? ' · đã gắn từ trước' : ''}
            </small>
          </span>
          {willAdd && (
            <Badge bg="success-subtle" text="success" className="flex-shrink-0">
              + sẽ gắn
            </Badge>
          )}
          {willRemove && (
            <Badge bg="danger-subtle" text="danger" className="flex-shrink-0">
              − sẽ gỡ
            </Badge>
          )}
        </label>
        {link && (
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            <span className="small text-muted">Thứ tự</span>
            <Form.Control
              key={`${link.id}-${link.order_no}`}
              type="number"
              size="sm"
              defaultValue={link.order_no}
              disabled={!canEdit || saving || busyOrderId === link.id}
              onBlur={(e) => handleChangeOrder(link, e.target.value)}
              style={{ width: 64, fontSize: 16 }}
              aria-label={`Thứ tự trong ca ${link.template_name}`}
            />
          </div>
        )}
      </div>
    );
  };

  const scopeLabel = scopeBranchId
    ? branchName || mainTemplates[0]?.branch_name || 'chi nhánh đang chọn'
    : 'mọi chi nhánh';

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="text-truncate">
          Ca áp dụng — {task?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {!canEdit && (
          <Alert variant="secondary" className="py-2 small mb-3">
            Bạn chỉ có quyền xem. Sửa liên kết task ↔ ca cần quyền Quản lý hoặc Admin.
          </Alert>
        )}
        {saveErrors.length > 0 && (
          <Alert variant="danger" className="py-2" data-testid="tsl-save-errors">
            <div className="fw-semibold mb-1">Có {saveErrors.length} ca chưa lưu được:</div>
            <ul className="mb-0 ps-3">
              {saveErrors.map((f, i) => (
                <li key={i}>
                  Chưa {f.action} được <strong>{f.name}</strong>: {f.message}
                </li>
              ))}
            </ul>
            <div className="small mt-1">Danh sách bên dưới đã tải lại theo dữ liệu thật.</div>
          </Alert>
        )}

        {loading && links.length === 0 ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Đang tải...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {links.length === 0 && (
              <Alert variant="warning" className="py-2 mb-3">
                Task này <strong>chưa được gắn vào ca nào</strong> — sẽ không tự sinh việc cho
                nhân viên khi mở ca.
              </Alert>
            )}

            <div className="small text-muted mb-2">
              Đang hiện các ca <strong className="text-primary">đang hoạt động</strong> của{' '}
              <strong className="text-primary">{scopeLabel}</strong> ({mainTemplates.length} ca)
            </div>

            {mainTemplates.length === 0 ? (
              <Alert variant="light" className="border py-2">
                Không có ca nào đang hoạt động{scopeBranchId ? ' ở chi nhánh này' : ''}.
              </Alert>
            ) : (
              <>
                <label
                  htmlFor={`tsl-all-${task?.id}`}
                  className="d-flex align-items-center gap-2 border border-2 border-primary rounded px-2 py-2 mb-2 bg-primary bg-opacity-10 fw-semibold"
                  style={{ ...ROW_STYLE, cursor: canEdit ? 'pointer' : 'default' }}
                >
                  <input
                    id={`tsl-all-${task?.id}`}
                    ref={selectAllRef}
                    type="checkbox"
                    className="form-check-input m-0"
                    style={CHECK_STYLE}
                    checked={allState === 'all'}
                    disabled={!canEdit || saving}
                    onChange={toggleAll}
                    data-testid="tsl-select-all"
                  />
                  <span className="flex-grow-1">Chọn tất cả {mainTemplates.length} ca</span>
                  <span className="small text-muted" data-testid="tsl-count">
                    {checkedInMain}/{mainTemplates.length}
                  </span>
                </label>

                {mainGroups.map((g) => (
                  <div key={g.key} className="mb-2">
                    {g.title && (
                      <div className="small fw-bold text-muted text-uppercase mt-3 mb-1">{g.title}</div>
                    )}
                    <div className="d-flex flex-column gap-2">
                      {g.items.map((t) =>
                        renderRow({
                          templateId: t.id,
                          title: t.name,
                          subtitle: hhmm(t.start_hour, t.start_minute),
                          isOther: false,
                        }),
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {otherLinks.length > 0 && (
              <div className="mt-3" data-testid="tsl-other">
                <div className="small fw-bold text-muted text-uppercase mb-1">
                  Ca khác đang gắn (ngoài chi nhánh / đang tạm dừng)
                </div>
                <div className="d-flex flex-column gap-2">
                  {otherLinks.map((l) =>
                    renderRow({
                      templateId: l.shift_template_id,
                      title: `${l.template_name}${l.branch_name ? ` · ${l.branch_name}` : ''}`,
                      subtitle:
                        `${hhmm(l.start_hour, l.start_minute)}` +
                        (l.template_is_active === false ? ' · ca đang tạm dừng' : '') +
                        ' · giữ nguyên, bỏ tick nếu muốn gỡ',
                      isOther: true,
                    }),
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-nowrap">
        <Button variant="secondary" className="flex-fill" style={{ fontSize: 16 }} onClick={onHide}>
          Đóng
        </Button>
        <Button
          variant="primary"
          className="flex-fill"
          style={{ fontSize: 16 }}
          disabled={!canEdit || !dirty || saving || loading}
          onClick={handleSave}
          data-testid="tsl-save"
        >
          {saving ? 'Đang lưu...' : `Lưu (gắn ${toAdd.length} · gỡ ${toRemove.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
